import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  IconButton,
  TableContainer,
  Table,
  Tbody,
  Tr,
  Td,
  Thead,
  Portal,
} from '@chakra-ui/react';
import { Profit } from './shared';
import { useTranslation } from 'react-i18next';
import { exchangeRateCalculation } from '@texas/utils/helpers/numberHelpers';
import { Icons } from '@texas/components/shared/Icons';
import useFormat from '@texas/hooks/useFormat';

interface MarginCalculationProps {
  purchasePrice: number;
  purchaseExchangeRate: number;
  salesPrice: number;
  salesExchangeRate: number;
  targetExchangeRate: number;
  targetCurrencyCode: string;
  profit: Profit;
  decimalsInPrice: number;
}

export function MarginCalculation(props: MarginCalculationProps) {
  const { t } = useTranslation();
  const { numberFormat } = useFormat();
  if (props.profit !== Profit.Bad) return null;

  return (
    <Popover trigger="hover">
      <PopoverTrigger>
        <IconButton
          variant="ghost"
          size="xs"
          icon={<Icons.Calculator />}
          aria-label="calculator"
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent minW={{ base: '100%', lg: 'max-content' }}>
          <PopoverArrow />
          <PopoverBody>
            <TableContainer>
              <Table
                size="sm"
                fontSize={12}
                css={{
                  td: { fontSize: '12px', padding: 0 },
                  ' td[data-operator="1"]': {
                    textAlign: 'center',
                    fontWeight: 'bold',
                  },
                }}
              >
                <Thead>
                  <Tr
                    css={{
                      td: {
                        fontWeight: 'bold',
                        minWidth: '16px',
                      },
                    }}
                  >
                    <Td />
                    <Td>{t('salesMatrix.price')}</Td>
                    <Td w={2} />
                    <Td>
                      {t('variant.marginCalculations.exchange')} <br />
                      {t('variant.marginCalculations.rate')}
                    </Td>
                    <Td w={2} />
                    <Td>
                      {t('variant.marginCalculations.target')} <br />
                      {t('variant.marginCalculations.exchangeRate')}
                      {` (${props.targetCurrencyCode})`}
                    </Td>
                    <Td w={2} />
                    <Td>{t('general.total')}</Td>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td pr="12px !important">
                      {t('variant.marginCalculations.purchase')}
                    </Td>
                    <Td>
                      {numberFormat(props.purchasePrice, props.decimalsInPrice)}
                    </Td>
                    <Td data-operator="1">*</Td>
                    <Td>{props.purchaseExchangeRate}</Td>
                    <Td data-operator="1">/</Td>
                    <Td>{props.targetExchangeRate}</Td>
                    <Td data-operator="1">=</Td>
                    <Td>
                      {numberFormat(
                        exchangeRateCalculation(
                          props.purchasePrice,
                          props.purchaseExchangeRate,
                          props.targetExchangeRate,
                          props.decimalsInPrice,
                        ),
                        props.decimalsInPrice,
                      )}
                    </Td>
                  </Tr>
                  <Tr color="texas.danger.100" _light={{ color: 'red.600' }}>
                    <Td pr={2}>{t('variant.marginCalculations.sales')}</Td>
                    <Td>
                      {numberFormat(props.salesPrice, props.decimalsInPrice)}
                    </Td>
                    <Td data-operator="1">*</Td>
                    <Td>{props.salesExchangeRate}</Td>
                    <Td data-operator="1">/</Td>
                    <Td>{props.targetExchangeRate}</Td>
                    <Td data-operator="1">=</Td>
                    <Td>
                      {numberFormat(
                        exchangeRateCalculation(
                          props.salesPrice,
                          props.salesExchangeRate,
                          props.targetExchangeRate,
                          props.decimalsInPrice,
                        ),
                        props.decimalsInPrice,
                      )}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
